import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Pagination,
  CircularProgress,
  Alert,
} from "@mui/material";
import { fetchCustomers } from "./api";
import { surface } from "./format";
import CustomersFilters from "./components/CustomersFilters";
import CustomersTable from "./components/CustomersTable";
import ClarityPopup from "../superAdminPages/registerUserlist/ClarityPopup";

const emptyFilters = { search: "", createdFrom: "", createdTo: "" };

const CustomersPage = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(emptyFilters);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortDir, setSortDir] = useState("DESC");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [clarityEmail, setClarityEmail] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    filters.createdFrom,
    filters.createdTo,
    sortBy,
    sortDir,
  ]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetchCustomers({
          page,
          limit: 20,
          search: debouncedSearch || undefined,
          createdFrom: filters.createdFrom || undefined,
          createdTo: filters.createdTo || undefined,
          sortBy,
          sortDir,
        });
        if (cancelled) return;
        setRows(res.data.data || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      } catch (err) {
        if (!cancelled)
          setError(err.response?.data?.message || "Failed to load customers");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [
    page,
    debouncedSearch,
    filters.createdFrom,
    filters.createdTo,
    sortBy,
    sortDir,
  ]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortBy(field);
      setSortDir(
        field === "createdAt" || field === "latestOrderOn" ? "DESC" : "ASC",
      );
    }
  };

  return (
    <Box
      sx={{ p: { xs: 2, md: 3 }, maxWidth: { xs: "100%", md: 1200, lg: 1600 } }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Typography
          sx={{ fontSize: "1.25rem", fontWeight: 600, color: "#111827" }}
        >
          Customers
        </Typography>
        <Typography sx={{ color: "#6b7280", fontSize: "0.875rem", mt: 0.25 }}>
          {total.toLocaleString()} subscribe users
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ ...surface, p: 2, mb: 2 }}>
        <CustomersFilters
          search={filters.search}
          createdFrom={filters.createdFrom}
          createdTo={filters.createdTo}
          onChange={(next) => setFilters((prev) => ({ ...prev, ...next }))}
          onClear={() => setFilters(emptyFilters)}
        />
      </Paper>

      <Paper elevation={0} sx={surface}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <CustomersTable
            rows={rows}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            onOpenCustomer={(id) => navigate(`/dashboard/customers/${id}`)}
            onOpenTab={(id, tab) =>
              navigate(`/dashboard/customers/${id}?tab=${tab}`)
            }
            onClarity={setClarityEmail}
          />
        )}
      </Paper>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            shape="rounded"
          />
        </Box>
      )}

      <ClarityPopup
        open={Boolean(clarityEmail)}
        onClose={() => setClarityEmail(null)}
        email={clarityEmail}
      />
    </Box>
  );
};

export default CustomersPage;
